import type { SafeHtml } from './safe-html';
import type { Store, Unsubscribe } from './store';

/**
 * Tüm bileşenlerin taban sınıfı. Light-DOM çalışır (global utility CSS'in
 * uygulanabilmesi için shadow root yok). Desen:
 *
 *  - template(): SafeHtml döner (bkz. safe-html.ts — tüm değerler otomatik
 *    kaçışlanır; XSS önlemi tip düzeyindedir, ham string kabul edilmez)
 *  - render(): şablonu parse edip replaceChildren ile basar, wire() çağırır
 *  - olaylar data-act nitelikleriyle KÖKTEN delegasyonla bağlanır → yeniden
 *    render dinleyicileri koparmaz
 *  - store abonelikleri bind() ile alınır; disconnect'te otomatik bırakılır
 */
export abstract class SmgElement extends HTMLElement {
  private subscriptions: Unsubscribe[] = [];
  private wiredEvents = new Set<string>();
  private actHandlers = new Map<string, Record<string, (el: HTMLElement, ev: Event) => void>>();

  connectedCallback(): void {
    this.render();
    this.onConnected();
  }

  disconnectedCallback(): void {
    for (const un of this.subscriptions) un();
    this.subscriptions = [];
    this.onDisconnected();
  }

  /** Bileşen içeriği — yalnız SafeHtml (html`` etiketiyle üretilir). */
  protected abstract template(): SafeHtml;

  /** Her render sonrası: form değerleri, focus, imperative dokunuşlar. */
  protected wire(): void {}

  protected onConnected(): void {}

  protected onDisconnected(): void {}

  protected render(): void {
    const markup = this.template();
    const fragment = document
      .createRange()
      .createContextualFragment(markup.value);
    this.replaceChildren(fragment);
    this.wire();
  }

  /** Store'a abone ol: varsayılan davranış her değişimde yeniden çizmek. */
  protected bind<T>(store: Store<T>, onChange?: (value: T) => void): void {
    this.subscriptions.push(
      store.subscribe((v) => (onChange ? onChange(v) : this.render())),
    );
  }

  protected addSubscription(un: Unsubscribe): void {
    this.subscriptions.push(un);
  }

  /**
   * data-act delegasyonu. Handler kaydı her render'da güncellenebilir;
   * dinleyici kökte bir kez kurulur.
   */
  protected onAct(
    type: string,
    handlers: Record<string, (el: HTMLElement, ev: Event) => void>,
  ): void {
    this.actHandlers.set(type, handlers);
    if (this.wiredEvents.has(type)) return;
    this.wiredEvents.add(type);
    this.addEventListener(type, (ev) => {
      const start = ev.target as HTMLElement | null;
      const target = start?.closest<HTMLElement>('[data-act]');
      if (!target || !this.contains(target)) return;
      const act = target.dataset['act'];
      if (!act) return;
      const fn = this.actHandlers.get(type)?.[act];
      if (fn) fn(target, ev);
    });
  }

  protected qs<T extends HTMLElement>(selector: string): T | null {
    return this.querySelector<T>(selector);
  }
}
